import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FiArrowLeft, FiCalendar, FiClock, FiMapPin, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import AdminLayout from "../../Layout/AdminLayout";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import {
  StarRatingDisplay,
  RATING_OPTIONS_DECIMAL,
  TickIcon,
  XIcon,
} from "../../Components/AdminShared";
import AvatarImage from "../../Components/AvatarImage/AvatarImage";
import { showAlert } from "../../feedback/feedbackApi";
import {
  DocumentPreview,
  downloadProviderDocument,
  getDocumentKind,
} from "./providerViewUtils";
import "./NewSignUps.scss";
import "./ProviderDirectoryDetail.scss";

const RATING_OPTIONS = RATING_OPTIONS_DECIMAL;

const DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SLOT_INTERVAL_OPTIONS = [10, 15, 20, 30, 60];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function newDraftRowKey() {
  return `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function cloneBlocksToDraft(blocks) {
  const sorted = sortSlotBlocks(blocks);
  return sorted.map((r) => ({
    _key: newDraftRowKey(),
    day: r?.day || "Mon",
    startTime: r?.startTime || "09:00",
    endTime: r?.endTime || "10:00",
    location: r?.location || "",
    clinicId: r?.clinicId != null ? String(r.clinicId) : "",
  }));
}

/**
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
function validateDraftSlotRows(rows) {
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const day = String(r.day || "").trim();
    const st = String(r.startTime || "").trim();
    const et = String(r.endTime || "").trim();
    const loc = String(r.location || "").trim();
    const hasAny = Boolean(day || st || et || loc || String(r.clinicId || "").trim());
    if (!hasAny) continue;
    if (!DAY_ORDER.includes(day)) {
      return { ok: false, message: `Row ${i + 1}: choose a valid weekday.` };
    }
    if (!TIME_RE.test(st) || !TIME_RE.test(et)) {
      return { ok: false, message: `Row ${i + 1}: use 24-hour times like 09:00 and 17:30.` };
    }
    if (st >= et) {
      return { ok: false, message: `Row ${i + 1}: end time must be after start time.` };
    }
    if (loc.length < 2) {
      return { ok: false, message: `Row ${i + 1}: location / clinic name is required (at least 2 characters).` };
    }
  }
  return { ok: true };
}

function ratingsRoughlyEqual(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return Math.abs(Number(a) - Number(b)) < 0.0001;
}

function daySortKey(day) {
  const d = String(day || "").trim();
  const i = DAY_ORDER.indexOf(d);
  return i >= 0 ? i : 99;
}

function sortSlotBlocks(blocks) {
  return [...(blocks || [])].sort((a, b) => {
    const da = daySortKey(a?.day) - daySortKey(b?.day);
    if (da !== 0) return da;
    return String(a?.startTime || "").localeCompare(String(b?.startTime || ""));
  });
}

function labelPracticeMode(mode) {
  const m = String(mode || "").toLowerCase();
  if (m === "online") return "Online consultations";
  if (m === "inclinic") return "In-clinic practice";
  return mode || "—";
}

function formatTrackTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
}

/** Turn specialization or comma-heavy strings into display tokens (dedupe). */
function tokensFromText(s) {
  if (!s || typeof s !== "string") return [];
  const parts = s.split(/[,;|]/).map((x) => x.trim()).filter(Boolean);
  return Array.from(new Set(parts));
}

function procedureChipsFromData(selectedSubCategoryNames, specializationFallback) {
  const fromArr = Array.isArray(selectedSubCategoryNames)
    ? selectedSubCategoryNames.map((x) => String(x).trim()).filter(Boolean)
    : [];
  if (fromArr.length) return fromArr;
  const fromSpec = tokensFromText(specializationFallback);
  if (fromSpec.length === 1 && fromSpec[0].length > 120) return [];
  return fromSpec;
}

function ProcedureTreeNode({ node }) {
  const lvl = Math.min(Math.max(Number(node.level) || 2, 2), 5);
  return (
    <li className={`pdd_proc_node pdd_proc_node--l${lvl}`}>
      <div className="pdd_proc_heading">
        <span className="pdd_proc_lvl_tag">L{lvl}</span>
        <span className="pdd_proc_name">{node.name}</span>
        {node.selected ? <span className="pdd_proc_sel">Selected</span> : null}
      </div>
      {node.description ? <p className="pdd_proc_desc">{node.description}</p> : null}
      {node.children?.length > 0 ? (
        <ol className={`pdd_proc_children pdd_proc_children--from_l${lvl}`}>
          {node.children.map((ch) => (
            <ProcedureTreeNode key={ch.id} node={ch} />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

function ProcedureHierarchyBlock({ hierarchy }) {
  if (!hierarchy?.length) return null;
  return (
    <div className="pdd_proc_wrap">
      <p className="pdd_proc_legend">
        <span className="pdd_proc_legend_item">
          <strong>L1</strong> Category
        </span>
        <span className="pdd_proc_legend_sep">·</span>
        <span className="pdd_proc_legend_item">
          <strong>L2–L5</strong> Nested subcategories from the directory
        </span>
      </p>
      <div className="pdd_proc_hierarchy">
        {hierarchy.map((cat) => (
          <article key={cat.categoryId} className="pdd_proc_l1">
            <header className="pdd_proc_l1_head">
              <span className="pdd_proc_lvl_tag pdd_proc_lvl_tag--l1">L1</span>
              <h3 className="pdd_proc_l1_name">{cat.categoryName}</h3>
            </header>
            <ol className="pdd_proc_l1_tree">
              {(cat.tree || []).map((node) => (
                <ProcedureTreeNode key={node.id} node={node} />
              ))}
            </ol>
          </article>
        ))}
      </div>
    </div>
  );
}

function mapApiToViewDoc(data) {
  return {
    profileImage: data?.image || "",
    medicalDegreeUrl: data?.medicalDegreeUrl || "",
    registrationCertificateUrl: data?.registrationCertificateUrl || "",
    profile: data
      ? {
          name: data?.name ?? "",
          email: data?.email ?? "",
          mobile: data?.mobile ?? "",
          emergencyContactNumber: data?.emergencyContactNumber ?? "",
          gender: data?.gender ?? "",
          degree: data?.degree ?? "",
          degrees: Array.isArray(data?.degrees) ? data.degrees : [],
          pendingDegreesEdit: data?.pendingDegreesEdit || null,
          specialization: data?.specialization ?? "",
          yearsOfExperience: data?.yearsOfExperience ?? "",
          registrationId: data?.registrationId ?? "",
          hospitalClinicName: data?.hospitalClinicName ?? "",
          hospitalType: data?.hospitalType ?? "",
          establishedYear: data?.establishedYear ?? "",
          licenseNumber: data?.licenseNumber ?? "",
          cityOfPractice: data?.cityOfPractice ?? "",
          state: data?.state ?? "",
          pincode: data?.pincode ?? "",
          status: data?.status ?? "",
          rejectionReason: data?.rejectionReason ?? "",
          isProfileComplete: data?.isProfileComplete ?? "",
          createdAt: data?.createdAt,
        }
      : null,
    selectedSubCategoryNames: data?.selectedSubCategoryNames ?? [],
    procedureHierarchy: Array.isArray(data?.procedureHierarchy) ? data.procedureHierarchy : [],
    procedureSelectionsTrackVersion:
      data?.procedureSelectionsTrackVersion != null ? Number(data.procedureSelectionsTrackVersion) : null,
    procedureSelectionsUpdatedAt: data?.procedureSelectionsUpdatedAt ?? null,
    practiceMode: data?.practiceMode ?? "",
    activeDays: Array.isArray(data?.activeDays) ? data.activeDays : [],
    preferredText: data?.preferredText ?? "",
    slotIntervalMinutes:
      data?.slotIntervalMinutes != null ? Number(data.slotIntervalMinutes) : 30,
    slotBlocks: Array.isArray(data?.slotBlocks) ? data.slotBlocks : [],
    clinicSchedules: Array.isArray(data?.clinicSchedules) ? data.clinicSchedules : [],
    emergency24x7: !!data?.emergency24x7,
    departmentOpdSchedules: Array.isArray(data?.departmentOpdSchedules)
      ? data.departmentOpdSchedules
      : [],
  };
}

function statusLabelFromApi(status) {
  if (!status) return "Pending";
  const s = String(status).toLowerCase();
  if (s === "approved") return "Approved";
  if (s === "rejected") return "Rejected";
  if (s === "pending_admin" || s === "pending_selection") return "Pending";
  return status;
}

function statusPillClass(label) {
  const x = String(label || "").toLowerCase();
  if (x === "approved") return "pdd_pill pdd_pill--ok";
  if (x === "rejected") return "pdd_pill pdd_pill--bad";
  if (x === "pending") return "pdd_pill pdd_pill--wait";
  return "pdd_pill";
}

function formatScoreOneDecimal(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toFixed(1);
}

export default function ProviderDirectoryDetail() {
  const { providerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const providerType = useMemo(() => {
    const p = location.pathname.toLowerCase();
    if (p.startsWith("/hospital-list/")) return "hospital";
    return "doctor";
  }, [location.pathname]);

  const listPath = providerType === "hospital" ? "/hospital-list" : "/doctor-list";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [headerName, setHeaderName] = useState("—");
  const [viewMeta, setViewMeta] = useState({
    status: "Pending",
    rawStatus: "",
    adminRating: null,
    patientRatingAverage: null,
    patientRatingCount: 0,
  });
  const [viewDoc, setViewDoc] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [ratingLocal, setRatingLocal] = useState(null);
  const [actionLoadingRate, setActionLoadingRate] = useState(false);
  /** Full-screen document viewer: { url, title } */
  const [docModal, setDocModal] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReasonInput, setRejectReasonInput] = useState("");
  const [reviewActionBusy, setReviewActionBusy] = useState(null);

  const [slotsEditing, setSlotsEditing] = useState(false);
  const [slotsDraft, setSlotsDraft] = useState([]);
  const [availMetaDraft, setAvailMetaDraft] = useState({
    practiceMode: "inClinic",
    activeDays: [],
    preferredText: "",
    slotIntervalMinutes: 30,
  });
  const [slotsSaving, setSlotsSaving] = useState(false);

  const procedureChips = useMemo(() => {
    if (!viewDoc) return [];
    return procedureChipsFromData(
      viewDoc.selectedSubCategoryNames,
      viewDoc.profile?.specialization
    );
  }, [viewDoc]);

  const canShowApproveReject = useMemo(
    () => ["pending_admin", "pending_selection"].includes(viewMeta.rawStatus),
    [viewMeta.rawStatus]
  );

  const sortedSlotBlocks = useMemo(
    () => sortSlotBlocks(viewDoc?.slotBlocks),
    [viewDoc?.slotBlocks]
  );

  const hasDoctorSlotData =
    providerType === "doctor" &&
    viewDoc &&
    (sortedSlotBlocks.length > 0 ||
      (viewDoc.clinicSchedules && viewDoc.clinicSchedules.length > 0) ||
      (viewDoc.activeDays && viewDoc.activeDays.length > 0) ||
      (viewDoc.preferredText && String(viewDoc.preferredText).trim().length > 0));

  const hasHospitalOpd =
    providerType === "hospital" &&
    viewDoc &&
    Array.isArray(viewDoc.departmentOpdSchedules) &&
    viewDoc.departmentOpdSchedules.length > 0;

  const beginEditDoctorSlots = useCallback(() => {
    if (!viewDoc || providerType !== "doctor") return;
    setSlotsDraft(cloneBlocksToDraft(viewDoc.slotBlocks));
    setAvailMetaDraft({
      practiceMode: viewDoc.practiceMode === "online" ? "online" : "inClinic",
      activeDays: Array.isArray(viewDoc.activeDays) ? [...viewDoc.activeDays] : [],
      preferredText: viewDoc.preferredText != null ? String(viewDoc.preferredText) : "",
      slotIntervalMinutes:
        viewDoc.slotIntervalMinutes != null && !Number.isNaN(Number(viewDoc.slotIntervalMinutes))
          ? Number(viewDoc.slotIntervalMinutes)
          : 30,
    });
    setSlotsEditing(true);
  }, [viewDoc, providerType]);

  const cancelEditDoctorSlots = useCallback(() => {
    setSlotsEditing(false);
    setSlotsDraft([]);
  }, []);

  const load = useCallback(async () => {
    if (!providerId) return;
    setLoading(true);
    setError(null);
    setViewDoc(null);
    try {
      const isHospital = providerType === "hospital";
      const res = await API.get(
        isHospital ? endpoints.adminHospitalById(providerId) : endpoints.adminDoctorById(providerId)
      );
      const data = res?.data?.data || res?.data;
      if (!data) {
        setError("Record not found.");
        setViewDoc(null);
        setLoading(false);
        return;
      }
      setHeaderName(data?.name || "—");
      setViewMeta({
        status: statusLabelFromApi(data?.status),
        rawStatus: String(data?.status || "").toLowerCase(),
        adminRating: data?.adminRating ?? null,
        patientRatingAverage: data?.patientRatingAverage ?? null,
        patientRatingCount: data?.patientRatingCount ?? 0,
      });
      setRatingLocal(data?.adminRating ?? null);
      setViewDoc(mapApiToViewDoc(data));
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to load provider.");
      setViewDoc(null);
    } finally {
      setLoading(false);
    }
  }, [providerId, providerType]);

  const saveDoctorSlots = useCallback(async () => {
    if (!providerId || providerType !== "doctor") return;
    const check = validateDraftSlotRows(slotsDraft);
    if (!check.ok) {
      showAlert(check.message, { variant: "warning", title: "Check slot rows" });
      return;
    }
    const slotBlocks = slotsDraft
      .map((r) => ({
        day: String(r.day || "").trim(),
        startTime: String(r.startTime || "").trim(),
        endTime: String(r.endTime || "").trim(),
        location: String(r.location || "").trim(),
        clinicId: String(r.clinicId || "").trim(),
      }))
      .filter((r) => {
        const hasAny = Boolean(
          r.day || r.startTime || r.endTime || r.location || r.clinicId
        );
        if (!hasAny) return false;
        return (
          DAY_ORDER.includes(r.day) &&
          TIME_RE.test(r.startTime) &&
          TIME_RE.test(r.endTime) &&
          r.startTime < r.endTime &&
          r.location.length >= 2
        );
      });
    setSlotsSaving(true);
    try {
      await API.patch(endpoints.adminDoctorAvailability(providerId), {
        practiceMode: availMetaDraft.practiceMode,
        activeDays: availMetaDraft.activeDays,
        preferredText: availMetaDraft.preferredText,
        slotIntervalMinutes: availMetaDraft.slotIntervalMinutes,
        slotBlocks,
      });
      showAlert("Availability and slots saved for this doctor.", {
        variant: "success",
        title: "Saved",
      });
      setSlotsEditing(false);
      setSlotsDraft([]);
      await load();
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not save availability.", {
        variant: "error",
        title: "Save failed",
      });
    } finally {
      setSlotsSaving(false);
    }
  }, [providerId, providerType, slotsDraft, availMetaDraft, load]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSlotsEditing(false);
    setSlotsDraft([]);
  }, [providerId]);

  useEffect(() => {
    if (!docModal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDocModal(null);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [docModal]);

  const setRate = async (value) => {
    if (!providerId) return;
    setActionLoadingRate(true);
    try {
      const url =
        providerType === "doctor"
          ? endpoints.adminDoctorRating(providerId)
          : endpoints.adminHospitalRating(providerId);
      await API.put(url, { rating: value });
      setRatingLocal(value);
      setViewMeta((m) => ({ ...m, adminRating: value }));
    } catch (err) {
      showAlert(err?.response?.data?.message || "Failed to set rating", {
        variant: "error",
        title: "Rating not saved",
      });
    } finally {
      setActionLoadingRate(false);
    }
  };

  const tryRateFromView = (value) => {
    if (viewMeta.status !== "Approved") {
      showAlert(
        "Please approve this " + (providerType === "doctor" ? "doctor" : "hospital") + " first before rating.",
        { variant: "warning", title: "Approve first" }
      );
      return;
    }
    setRate(value);
  };

  const approveFromDetail = async () => {
    if (!providerId) return;
    setReviewActionBusy("approve");
    try {
      const url =
        providerType === "doctor"
          ? endpoints.adminDoctorApprove(providerId)
          : endpoints.adminHospitalApprove(providerId);
      await API.post(url);
      showAlert("Provider approved.", { variant: "success", title: "Approved" });
      await load();
    } catch (err) {
      showAlert(err?.response?.data?.message || "Approve failed", {
        variant: "error",
        title: "Approve failed",
      });
    } finally {
      setReviewActionBusy(null);
    }
  };

  const submitRejectFromDetail = async () => {
    const reason = String(rejectReasonInput || "").trim();
    if (!reason) {
      showAlert("Rejection reason is required.", { variant: "warning", title: "Reason required" });
      return;
    }
    if (!providerId) return;
    setReviewActionBusy("reject");
    try {
      const url =
        providerType === "doctor"
          ? endpoints.adminDoctorReject(providerId)
          : endpoints.adminHospitalReject(providerId);
      await API.post(url, { rejectionReason: reason });
      setRejectModalOpen(false);
      setRejectReasonInput("");
      showAlert("Provider rejected.", { variant: "success", title: "Rejected" });
      await load();
    } catch (err) {
      showAlert(err?.response?.data?.message || "Reject failed", {
        variant: "error",
        title: "Reject failed",
      });
    } finally {
      setReviewActionBusy(null);
    }
  };

  const approveDegreesEdit = async () => {
    if (!providerId || providerType !== "doctor") return;
    setReviewActionBusy("degrees-approve");
    try {
      await API.post(endpoints.adminDoctorDegreesApprove(providerId));
      showAlert("Degree changes approved.", { variant: "success", title: "Degrees approved" });
      await load();
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not approve degrees", {
        variant: "error",
        title: "Approve failed",
      });
    } finally {
      setReviewActionBusy(null);
    }
  };

  const rejectDegreesEdit = async () => {
    if (!providerId || providerType !== "doctor") return;
    const reason = window.prompt("Optional reason for rejecting degree changes:") || "";
    setReviewActionBusy("degrees-reject");
    try {
      await API.post(endpoints.adminDoctorDegreesReject(providerId), {
        rejectionReason: String(reason).trim(),
      });
      showAlert("Degree changes rejected.", { variant: "success", title: "Degrees rejected" });
      await load();
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not reject degrees", {
        variant: "error",
        title: "Reject failed",
      });
    } finally {
      setReviewActionBusy(null);
    }
  };

  const platformScore = ratingLocal ?? viewMeta.adminRating ?? null;
  const patientAvg = viewMeta.patientRatingAverage ?? null;
  const platformScoreStr = formatScoreOneDecimal(platformScore);
  const patientAvgStr = formatScoreOneDecimal(patientAvg);
  const canSetPlatformRating = viewMeta.status === "Approved";

  const title = providerType === "doctor" ? `${headerName}` : `${headerName}`;

  return (
    <AdminLayout>
      <div className="provider_directory_detail">
        <div className="pdd_toolbar pdd_toolbar_row">
          <button type="button" className="pdd_back" onClick={() => navigate(listPath)}>
            <FiArrowLeft aria-hidden />
            Back to list
          </button>
          {canShowApproveReject && !loading && viewDoc ? (
            <div className="pdd_toolbar_actions" role="group" aria-label="Approval actions">
              <button
                type="button"
                className="btn_approve btn_icon pdd_review_btn"
                disabled={reviewActionBusy === "approve"}
                onClick={approveFromDetail}
                title="Approve provider"
              >
                {reviewActionBusy === "approve" ? "…" : <TickIcon />}
                <span className="pdd_review_btn_label">Approve</span>
              </button>
              <button
                type="button"
                className="btn_reject btn_icon pdd_review_btn"
                disabled={reviewActionBusy === "reject"}
                onClick={() => {
                  setRejectReasonInput("");
                  setRejectModalOpen(true);
                }}
                title="Reject provider"
              >
                <XIcon />
                <span className="pdd_review_btn_label">Reject</span>
              </button>
            </div>
          ) : null}
        </div>

        {error && !loading && (
          <div className="pdd_error" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="pdd_card pdd_card--loading">
            <p className="pdd_muted">Loading provider…</p>
          </div>
        ) : viewDoc ? (
          <>
            <header className="pdd_hero">
              <div className="pdd_hero_avatar">
                <AvatarImage
                  src={viewDoc.profileImage}
                  alt=""
                  name={headerName}
                  className="pdd_avatar_img"
                  fallbackClassName="pdd_avatar_fallback"
                />
              </div>
              <div className="pdd_hero_main">
                <div className="pdd_hero_top">
                  <h1 className="pdd_hero_title">{title}</h1>
                  <span className={statusPillClass(viewMeta.status)}>{viewMeta.status}</span>
                </div>
                <p className="pdd_hero_sub">
                  {providerType === "doctor" ? "Doctor" : "Hospital"}
                  {viewDoc.profile?.degree ? ` · ${viewDoc.profile.degree}` : ""}
                  {viewDoc.profile?.hospitalClinicName
                    ? ` · ${viewDoc.profile.hospitalClinicName}`
                    : ""}
                </p>
                {(viewDoc.profile?.cityOfPractice || viewDoc.profile?.state) && (
                  <p className="pdd_hero_loc">
                    <FiMapPin aria-hidden className="pdd_hero_loc_icon" />
                    {[viewDoc.profile.cityOfPractice, viewDoc.profile.state, viewDoc.profile.pincode]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
                {viewMeta.status === "Rejected" && String(viewDoc.profile?.rejectionReason || "").trim() ? (
                  <p className="pdd_hero_reject_note">
                    <strong>Rejection reason:</strong> {viewDoc.profile.rejectionReason}
                  </p>
                ) : null}
              </div>
            </header>

            <div className="pdd_grid">
              <section className="pdd_card pdd_section">
                <h2 className="pdd_section_title">Contact & registration</h2>
                <dl className="pdd_dl">
                  <div className="pdd_dl_row">
                    <dt>Email</dt>
                    <dd>{viewDoc.profile?.email || "—"}</dd>
                  </div>
                  <div className="pdd_dl_row">
                    <dt>Phone</dt>
                    <dd>{viewDoc.profile?.mobile || "—"}</dd>
                  </div>
                  <div className="pdd_dl_row">
                    <dt>{providerType === "hospital" ? "License / Reg. no." : "Registration ID"}</dt>
                    <dd>
                      {providerType === "hospital"
                        ? viewDoc.profile?.licenseNumber || viewDoc.profile?.registrationId || "—"
                        : viewDoc.profile?.registrationId || "—"}
                    </dd>
                  </div>
                  <div className="pdd_dl_row">
                    <dt>Signed up</dt>
                    <dd>
                      {viewDoc.profile?.createdAt
                        ? new Date(viewDoc.profile.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </dd>
                  </div>
                </dl>
              </section>

              <section className="pdd_card pdd_section">
                <h2 className="pdd_section_title">Clinical profile</h2>
                <dl className="pdd_dl">
                  <div className="pdd_dl_row">
                    <dt>{providerType === "hospital" ? "Hospital type" : "Years of experience"}</dt>
                    <dd>
                      {providerType === "hospital"
                        ? viewDoc.profile?.hospitalType || "—"
                        : viewDoc.profile?.yearsOfExperience != null &&
                            viewDoc.profile?.yearsOfExperience !== ""
                          ? viewDoc.profile.yearsOfExperience
                          : "—"}
                    </dd>
                  </div>
                  {providerType === "doctor" ? (
                    <div className="pdd_dl_row">
                      <dt>Medical degrees</dt>
                      <dd>
                        {(viewDoc.profile?.degrees || []).length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {viewDoc.profile.degrees.map((d, i) => (
                              <li key={`deg-${i}`}>
                                <strong>{d.title || "—"}</strong>
                                {d.institution ? ` · ${d.institution}` : ""}
                                {d.year ? ` · ${d.year}` : ""}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          viewDoc.profile?.degree || "—"
                        )}
                      </dd>
                    </div>
                  ) : null}
                  {providerType === "doctor" && viewDoc.profile?.pendingDegreesEdit?.degrees?.length ? (
                    <div className="pdd_dl_row">
                      <dt>Pending degree update</dt>
                      <dd>
                        <p className="pdd_muted" style={{ marginTop: 0 }}>
                          Doctor requested these changes for admin approval
                          {viewDoc.profile.pendingDegreesEdit.requestedAt
                            ? ` · ${new Date(viewDoc.profile.pendingDegreesEdit.requestedAt).toLocaleString("en-IN")}`
                            : ""}
                          .
                        </p>
                        <ul style={{ margin: "0 0 12px", paddingLeft: 18 }}>
                          {viewDoc.profile.pendingDegreesEdit.degrees.map((d, i) => (
                            <li key={`pdeg-${i}`}>
                              <strong>{d.title || "—"}</strong>
                              {d.institution ? ` · ${d.institution}` : ""}
                              {d.year ? ` · ${d.year}` : ""}
                            </li>
                          ))}
                        </ul>
                        <div className="pdd_toolbar_actions" role="group" aria-label="Degree approval">
                          <button
                            type="button"
                            className="pdd_btn_primary"
                            disabled={!!reviewActionBusy}
                            onClick={approveDegreesEdit}
                          >
                            {reviewActionBusy === "degrees-approve" ? "Approving…" : "Approve degrees"}
                          </button>
                          <button
                            type="button"
                            className="pdd_btn_secondary"
                            disabled={!!reviewActionBusy}
                            onClick={rejectDegreesEdit}
                          >
                            {reviewActionBusy === "degrees-reject" ? "Rejecting…" : "Reject degrees"}
                          </button>
                        </div>
                      </dd>
                    </div>
                  ) : null}
                  {providerType === "hospital" && (
                    <div className="pdd_dl_row">
                      <dt>Established</dt>
                      <dd>
                        {viewDoc.profile?.establishedYear != null &&
                        viewDoc.profile?.establishedYear !== ""
                          ? viewDoc.profile.establishedYear
                          : "—"}
                      </dd>
                    </div>
                  )}
                  {providerType === "hospital" && (
                    <div className="pdd_dl_row">
                      <dt>Emergency contact</dt>
                      <dd>{viewDoc.profile?.emergencyContactNumber || "—"}</dd>
                    </div>
                  )}
                </dl>
              </section>

              {(viewDoc.procedureHierarchy?.length > 0 || procedureChips.length > 0) && (
                <section className="pdd_card pdd_section pdd_section--wide">
                  <h2 className="pdd_section_title">Procedures & sub-categories</h2>
                  {viewDoc.procedureSelectionsTrackVersion != null &&
                  viewDoc.procedureSelectionsTrackVersion >= 1 ? (
                    <p className="pdd_proc_track_line">
                      Catalog-linked selections{" "}
                      <span className="pdd_proc_track_badge">v{viewDoc.procedureSelectionsTrackVersion}</span>
                      {viewDoc.procedureSelectionsUpdatedAt
                        ? ` · saved ${formatTrackTimestamp(viewDoc.procedureSelectionsUpdatedAt)}`
                        : null}
                    </p>
                  ) : null}
                  {viewDoc.procedureHierarchy?.length > 0 ? (
                    <ProcedureHierarchyBlock hierarchy={viewDoc.procedureHierarchy} />
                  ) : (
                    <>
                      <p className="pdd_proc_fallback_note">
                        {viewDoc.procedureSelectionsTrackVersion != null &&
                        viewDoc.procedureSelectionsTrackVersion >= 1
                          ? "Could not build the category tree from stored procedure IDs (catalog data may have changed). Showing flat names from saved selections."
                          : "Category tree unavailable from the API (e.g. subcategory IDs not linked). Showing flat names."}
                      </p>
                      <ul className="pdd_chips" aria-label="Procedures">
                        {procedureChips.map((label) => (
                          <li key={label} className="pdd_chip">
                            {label}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </section>
              )}

              {providerType === "doctor" && (
                <section className="pdd_card pdd_section pdd_section--wide">
                  <div className="pdd_section_head">
                    <h2 className="pdd_section_title pdd_section_title--inline">
                      <FiCalendar className="pdd_section_icon" aria-hidden />
                      Availability & slots
                    </h2>
                    {!slotsEditing ? (
                      <button
                        type="button"
                        className="pdd_btn_edit_slots"
                        onClick={beginEditDoctorSlots}
                        title="Edit weekly blocks and booking settings for this doctor"
                      >
                        <FiEdit2 aria-hidden />
                        Edit slots
                      </button>
                    ) : (
                      <div className="pdd_slot_edit_actions">
                        <button
                          type="button"
                          className="pdd_btn_secondary"
                          disabled={slotsSaving}
                          onClick={cancelEditDoctorSlots}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="pdd_btn_save_slots"
                          disabled={slotsSaving}
                          onClick={saveDoctorSlots}
                        >
                          {slotsSaving ? "Saving…" : "Save"}
                        </button>
                      </div>
                    )}
                  </div>
                  {!slotsEditing ? (
                    <>
                      <dl className="pdd_dl">
                        <div className="pdd_dl_row">
                          <dt>Practice mode</dt>
                          <dd>{labelPracticeMode(viewDoc.practiceMode)}</dd>
                        </div>
                        <div className="pdd_dl_row">
                          <dt>
                            <FiClock className="pdd_inline_icon" aria-hidden />
                            Slot length
                          </dt>
                          <dd>{viewDoc.slotIntervalMinutes || 30} minutes</dd>
                        </div>
                        <div className="pdd_dl_row">
                          <dt>Active days (signup)</dt>
                          <dd>
                            {viewDoc.activeDays?.length
                              ? viewDoc.activeDays.join(", ")
                              : "—"}
                          </dd>
                        </div>
                      </dl>
                      {viewDoc.preferredText ? (
                        <div className="pdd_note">
                          <strong>Preferred hours (text)</strong>
                          <p>{viewDoc.preferredText}</p>
                        </div>
                      ) : null}

                      {sortedSlotBlocks.length > 0 ? (
                        <div className="pdd_table_wrap">
                          <table className="pdd_table">
                            <caption className="pdd_sr_only">Weekly slot blocks</caption>
                            <thead>
                              <tr>
                                <th scope="col">Day</th>
                                <th scope="col">Time</th>
                                <th scope="col">Location</th>
                                <th scope="col">Clinic ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedSlotBlocks.map((row, idx) => (
                                <tr key={`${row.day}-${row.startTime}-${idx}`}>
                                  <td>{row.day || "—"}</td>
                                  <td>
                                    {(row.startTime || "—") + " – " + (row.endTime || "—")}
                                  </td>
                                  <td>{row.location || "—"}</td>
                                  <td className="pdd_mono">{row.clinicId || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}

                      {viewDoc.clinicSchedules?.length > 0 ? (
                        <div className="pdd_clinic_list">
                          {viewDoc.clinicSchedules.map((clinic) => (
                            <div key={clinic.clinicId || clinic.clinicName} className="pdd_clinic_card">
                              <h3 className="pdd_clinic_name">{clinic.clinicName || "Clinic"}</h3>
                              {clinic.address ? (
                                <p className="pdd_clinic_addr">{clinic.address}</p>
                              ) : null}
                              {Array.isArray(clinic.slots) && clinic.slots.length > 0 ? (
                                <table className="pdd_table pdd_table--compact">
                                  <thead>
                                    <tr>
                                      <th>Day</th>
                                      <th>Time</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {clinic.slots.map((s, i) => (
                                      <tr key={`${s.day}-${i}`}>
                                        <td>{s.day}</td>
                                        <td>
                                          {s.startTime} – {s.endTime}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="pdd_muted">No per-clinic day rows.</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {!hasDoctorSlotData && (
                        <p className="pdd_empty_hint">
                          No custom weekly blocks on file. Patient booking may still use default time
                          templates until slots are added here or in the doctor portal.
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="pdd_slots_editor">
                      <p className="pdd_slots_editor_hint">
                        Helbid ops can set the same weekly blocks the doctor sees in their portal. Times must
                        be 24-hour (HH:MM). Saving overwrites derived per-clinic rows to match this table.
                      </p>
                      <div className="pdd_slots_meta_grid">
                        <label className="pdd_slots_field">
                          <span>Practice mode</span>
                          <select
                            value={availMetaDraft.practiceMode}
                            onChange={(e) =>
                              setAvailMetaDraft((m) => ({
                                ...m,
                                practiceMode: e.target.value === "online" ? "online" : "inClinic",
                              }))
                            }
                          >
                            <option value="inClinic">In-clinic</option>
                            <option value="online">Online</option>
                          </select>
                        </label>
                        <label className="pdd_slots_field">
                          <span>Slot length (minutes)</span>
                          <select
                            value={availMetaDraft.slotIntervalMinutes}
                            onChange={(e) =>
                              setAvailMetaDraft((m) => ({
                                ...m,
                                slotIntervalMinutes: Number(e.target.value) || 30,
                              }))
                            }
                          >
                            {SLOT_INTERVAL_OPTIONS.map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <fieldset className="pdd_slots_active_days">
                        <legend>Active days (signup)</legend>
                        <div className="pdd_slots_day_checks">
                          {DAY_ORDER.map((d) => {
                            const on = availMetaDraft.activeDays.includes(d);
                            return (
                              <label key={d} className="pdd_slots_day_check">
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => {
                                    setAvailMetaDraft((m) => ({
                                      ...m,
                                      activeDays: on
                                        ? m.activeDays.filter((x) => x !== d)
                                        : [...m.activeDays, d].sort(
                                            (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
                                          ),
                                    }));
                                  }}
                                />
                                {d}
                              </label>
                            );
                          })}
                        </div>
                      </fieldset>
                      <label className="pdd_slots_field pdd_slots_field--full">
                        <span>Preferred hours / internal notes (optional)</span>
                        <textarea
                          className="pdd_slots_textarea"
                          rows={2}
                          value={availMetaDraft.preferredText}
                          onChange={(e) =>
                            setAvailMetaDraft((m) => ({ ...m, preferredText: e.target.value }))
                          }
                          placeholder="Shown to ops; optional context for patients if you use this field elsewhere"
                        />
                      </label>

                      <div className="pdd_slots_table_toolbar">
                        <span className="pdd_slots_table_label">Weekly blocks</span>
                        <button
                          type="button"
                          className="pdd_btn_add_slot"
                          onClick={() =>
                            setSlotsDraft((rows) => [
                              ...rows,
                              {
                                _key: newDraftRowKey(),
                                day: "Mon",
                                startTime: "09:00",
                                endTime: "10:00",
                                location: "",
                                clinicId: "",
                              },
                            ])
                          }
                        >
                          <FiPlus aria-hidden />
                          Add row
                        </button>
                      </div>
                      <div className="pdd_table_wrap pdd_table_wrap--slots_edit">
                        <table className="pdd_table pdd_table--slots_edit">
                          <thead>
                            <tr>
                              <th scope="col">Day</th>
                              <th scope="col">Start</th>
                              <th scope="col">End</th>
                              <th scope="col">Location</th>
                              <th scope="col">Clinic ID</th>
                              <th scope="col" className="pdd_th_actions">
                                <span className="pdd_sr_only">Remove</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {slotsDraft.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="pdd_slots_empty_row">
                                  No rows yet. Use &quot;Add row&quot; to create a slot block.
                                </td>
                              </tr>
                            ) : (
                              slotsDraft.map((row, idx) => (
                                <tr key={row._key}>
                                  <td>
                                    <select
                                      className="pdd_slots_cell_select"
                                      value={row.day}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setSlotsDraft((rs) =>
                                          rs.map((r, i) => (i === idx ? { ...r, day: v } : r))
                                        );
                                      }}
                                    >
                                      {DAY_ORDER.map((d) => (
                                        <option key={d} value={d}>
                                          {d}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="time"
                                      className="pdd_slots_time"
                                      value={row.startTime}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setSlotsDraft((rs) =>
                                          rs.map((r, i) => (i === idx ? { ...r, startTime: v } : r))
                                        );
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="time"
                                      className="pdd_slots_time"
                                      value={row.endTime}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setSlotsDraft((rs) =>
                                          rs.map((r, i) => (i === idx ? { ...r, endTime: v } : r))
                                        );
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="pdd_slots_text"
                                      value={row.location}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setSlotsDraft((rs) =>
                                          rs.map((r, i) => (i === idx ? { ...r, location: v } : r))
                                        );
                                      }}
                                      placeholder="Hospital or clinic name"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="pdd_slots_text pdd_mono"
                                      value={row.clinicId}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setSlotsDraft((rs) =>
                                          rs.map((r, i) => (i === idx ? { ...r, clinicId: v } : r))
                                        );
                                      }}
                                      placeholder="Auto if empty"
                                    />
                                  </td>
                                  <td className="pdd_td_actions">
                                    <button
                                      type="button"
                                      className="pdd_btn_icon_remove"
                                      title="Remove row"
                                      aria-label={`Remove slot row ${idx + 1}`}
                                      onClick={() =>
                                        setSlotsDraft((rs) => rs.filter((_, i) => i !== idx))
                                      }
                                    >
                                      <FiTrash2 aria-hidden />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {providerType === "hospital" && (
                <section className="pdd_card pdd_section pdd_section--wide">
                  <h2 className="pdd_section_title">
                    <FiCalendar className="pdd_section_icon" aria-hidden />
                    OPD & slot-style schedules
                  </h2>
                  <p className="pdd_muted pdd_tight_top">
                    Emergency 24×7:{" "}
                    <strong>{viewDoc.emergency24x7 ? "Yes" : "No"}</strong>
                  </p>
                  {hasHospitalOpd ? (
                    <div className="pdd_table_wrap">
                      <table className="pdd_table">
                        <thead>
                          <tr>
                            <th scope="col">Department</th>
                            <th scope="col">Weekdays</th>
                            <th scope="col">Hours</th>
                            <th scope="col">Slot size</th>
                            <th scope="col">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewDoc.departmentOpdSchedules.map((row, idx) => (
                            <tr key={`${row.departmentName}-${idx}`}>
                              <td>{row.departmentName || "—"}</td>
                              <td>
                                {Array.isArray(row.weekdays) && row.weekdays.length
                                  ? row.weekdays.join(", ")
                                  : "—"}
                              </td>
                              <td>
                                {(row.startTime || "—") + " – " + (row.endTime || "—")}
                              </td>
                              <td>
                                {row.slotDurationMinutes != null
                                  ? `${row.slotDurationMinutes} min`
                                  : "—"}
                              </td>
                              <td className="pdd_cell_notes">{row.notes || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="pdd_empty_hint">
                      No department OPD rows on file. Update hospital signup data if schedules should
                      appear here.
                    </p>
                  )}
                </section>
              )}

              <section className="pdd_card pdd_section pdd_section--wide">
                <h2 className="pdd_section_title">Documents</h2>
                <p className="pdd_muted pdd_docs_intro">
                  Signup uploads: profile photo plus verification documents below (same files as submitted at
                  registration).
                </p>
                <div className="pdd_docs">
                  {viewDoc.profileImage ? (
                    <div className="doc_block pdd_doc_block">
                      <h3 className="pdd_doc_heading">
                        {providerType === "hospital" ? "Facility / logo photo" : "Profile photo"}
                      </h3>
                      <DocumentPreview
                        url={viewDoc.profileImage}
                        title={providerType === "hospital" ? "Facility photo" : "Profile photo"}
                        compact
                      />
                      <div className="doc_actions">
                        <button
                          type="button"
                          onClick={() =>
                            setDocModal({
                              url: viewDoc.profileImage,
                              title: providerType === "hospital" ? "Facility photo" : "Profile photo",
                            })
                          }
                        >
                          View full screen
                        </button>
                        {providerType === "doctor" && (
                          <button
                            type="button"
                            className="pdd_btn_secondary"
                            disabled={downloading === "profile"}
                            onClick={async () => {
                              setDownloading("profile");
                              await downloadProviderDocument(
                                providerType,
                                providerId,
                                "profile",
                                "profile-photo"
                              );
                              setDownloading(null);
                            }}
                          >
                            {downloading === "profile" ? "Downloading…" : "Download"}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="pdd_doc_missing">Profile photo not uploaded.</p>
                  )}

                  {viewDoc.medicalDegreeUrl ? (
                    <div className="doc_block pdd_doc_block">
                      <h3 className="pdd_doc_heading">
                        {providerType === "hospital"
                          ? "Hospital registration certificate"
                          : "Medical degree"}
                      </h3>
                      <DocumentPreview
                        url={viewDoc.medicalDegreeUrl}
                        title={
                          providerType === "hospital"
                            ? "Hospital registration certificate"
                            : "Medical degree"
                        }
                        compact
                      />
                      <div className="doc_actions">
                        <button
                          type="button"
                          onClick={() =>
                            setDocModal({
                              url: viewDoc.medicalDegreeUrl,
                              title:
                                providerType === "hospital"
                                  ? "Hospital registration certificate"
                                  : "Medical degree",
                            })
                          }
                        >
                          View full screen
                        </button>
                        {providerType === "doctor" && (
                          <button
                            type="button"
                            className="pdd_btn_secondary"
                            disabled={downloading === "md"}
                            onClick={async () => {
                              setDownloading("md");
                              await downloadProviderDocument(
                                providerType,
                                providerId,
                                "medicalDegree",
                                "medical-degree"
                              );
                              setDownloading(null);
                            }}
                          >
                            {downloading === "md" ? "Downloading…" : "Download"}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="pdd_doc_missing">
                      {providerType === "hospital"
                        ? "Hospital registration certificate not uploaded."
                        : "Medical degree not uploaded."}
                    </p>
                  )}

                  {viewDoc.registrationCertificateUrl ? (
                    <div className="doc_block pdd_doc_block">
                      <h3 className="pdd_doc_heading">
                        {providerType === "hospital"
                          ? "Establishment / compliance certificate"
                          : "Registration certificate"}
                      </h3>
                      <DocumentPreview
                        url={viewDoc.registrationCertificateUrl}
                        title={
                          providerType === "hospital"
                            ? "Establishment / compliance certificate"
                            : "Registration certificate"
                        }
                        compact
                      />
                      <div className="doc_actions">
                        <button
                          type="button"
                          onClick={() =>
                            setDocModal({
                              url: viewDoc.registrationCertificateUrl,
                              title:
                                providerType === "hospital"
                                  ? "Establishment / compliance certificate"
                                  : "Registration certificate",
                            })
                          }
                        >
                          View full screen
                        </button>
                        {providerType === "doctor" && (
                          <button
                            type="button"
                            className="pdd_btn_secondary"
                            disabled={downloading === "rc"}
                            onClick={async () => {
                              setDownloading("rc");
                              await downloadProviderDocument(
                                providerType,
                                providerId,
                                "registrationCertificate",
                                "registration-certificate"
                              );
                              setDownloading(null);
                            }}
                          >
                            {downloading === "rc" ? "Downloading…" : "Download"}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="pdd_doc_missing">
                      {providerType === "hospital"
                        ? "Establishment / compliance certificate not uploaded."
                        : "Registration certificate not uploaded."}
                    </p>
                  )}
                </div>
              </section>

              <section className="pdd_card pdd_section pdd_section--wide pdd_section--rating">
                <h2 className="pdd_section_title">Ratings</h2>
                <div className="pdd_ratings">
                  <div className="pdd_ratings_overview">
                    <article className="pdd_ratings_card pdd_ratings_card--platform">
                      <span className="pdd_ratings_card_tag">Admin</span>
                      <h3 className="pdd_ratings_card_title">Platform score</h3>
                      <p className="pdd_ratings_card_hint">Your curated quality score for matching</p>
                      <div className="pdd_ratings_stars_row">
                        <div
                          className="pdd_ratings_stars pdd_ratings_stars--lg"
                          aria-hidden={platformScore == null}
                        >
                          <StarRatingDisplay rating={platformScore} />
                        </div>
                        <div className="pdd_ratings_score_block">
                          {platformScoreStr != null ? (
                            <>
                              <span className="pdd_ratings_score_big">{platformScoreStr}</span>
                              <span className="pdd_ratings_score_out">/ 5.0</span>
                            </>
                          ) : (
                            <span className="pdd_ratings_score_empty">Not set</span>
                          )}
                        </div>
                      </div>
                    </article>
                    <article className="pdd_ratings_card pdd_ratings_card--patient">
                      <span className="pdd_ratings_card_tag pdd_ratings_card_tag--patient">Patients</span>
                      <h3 className="pdd_ratings_card_title">Patient average</h3>
                      <p className="pdd_ratings_card_hint">From app feedback after completed care</p>
                      <div className="pdd_ratings_stars_row">
                        <div className="pdd_ratings_stars pdd_ratings_stars--lg">
                          <StarRatingDisplay rating={patientAvg} />
                        </div>
                        <div className="pdd_ratings_score_block">
                          {patientAvgStr != null ? (
                            <>
                              <span className="pdd_ratings_score_big">{patientAvgStr}</span>
                              <span className="pdd_ratings_score_out">/ 5.0</span>
                            </>
                          ) : (
                            <span className="pdd_ratings_score_empty">—</span>
                          )}
                        </div>
                      </div>
                      <p className="pdd_ratings_patient_meta">
                        {viewMeta.patientRatingCount > 0
                          ? `${viewMeta.patientRatingCount} rating${
                              viewMeta.patientRatingCount === 1 ? "" : "s"
                            }`
                          : "No patient ratings yet"}
                      </p>
                    </article>
                  </div>

                  <div className="pdd_ratings_editor">
                    <div
                      className={`pdd_ratings_callout${canSetPlatformRating ? "" : " pdd_ratings_callout--muted"}`}
                      role="note"
                    >
                      <p>
                        {canSetPlatformRating
                          ? "Choose a score from 1.0 to 5.0 (half-point steps). Patients rate separately in the app after care."
                          : `Approve this ${providerType === "doctor" ? "doctor" : "hospital"} before you can set a platform score.`}
                      </p>
                    </div>
                    <div className="pdd_ratings_editor_head">
                      <span className="pdd_ratings_editor_label">Set platform score</span>
                      {actionLoadingRate ? (
                        <span className="pdd_ratings_saving">Saving…</span>
                      ) : null}
                    </div>
                    <div className="pdd_ratings_scale" role="group" aria-label="Platform rating 1 to 5">
                      {RATING_OPTIONS.map((r) => (
                        <button
                          key={r}
                          type="button"
                          className={`pdd_rate_btn${ratingsRoughlyEqual(platformScore, r) ? " pdd_rate_btn--active" : ""}`}
                          disabled={!canSetPlatformRating || actionLoadingRate}
                          onClick={() => tryRateFromView(r)}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                    <footer className="pdd_ratings_footer">
                      <span className="pdd_ratings_footer_label">Saved admin score</span>
                      <span className="pdd_ratings_footer_value">
                        {platformScoreStr != null ? platformScoreStr : "—"}
                      </span>
                    </footer>
                  </div>
                </div>
              </section>
            </div>
          </>
        ) : null}

        {docModal ? (
          <div
            className="pdd_doc_modal_overlay"
            onClick={() => setDocModal(null)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pdd-doc-modal-title"
          >
            <div className="pdd_doc_modal" onClick={(e) => e.stopPropagation()}>
              <header className="pdd_doc_modal_head">
                <h2 id="pdd-doc-modal-title" className="pdd_doc_modal_title">
                  {docModal.title}
                </h2>
                <button
                  type="button"
                  className="pdd_doc_modal_close"
                  onClick={() => setDocModal(null)}
                  aria-label="Close document viewer"
                >
                  ×
                </button>
              </header>
              <div className="pdd_doc_modal_body">
                {getDocumentKind(docModal.url) === "pdf" ? (
                  <iframe
                    src={docModal.url}
                    title={docModal.title}
                    className="pdd_doc_modal_frame"
                  />
                ) : getDocumentKind(docModal.url) === "image" ? (
                  <img src={docModal.url} alt={docModal.title} className="pdd_doc_modal_img" />
                ) : (
                  <p className="pdd_doc_modal_fallback">
                    Inline preview is not available for this file type.{" "}
                    <a href={docModal.url} target="_blank" rel="noopener noreferrer">
                      Open in new tab
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {rejectModalOpen ? (
          <div
            className="ns_overlay"
            onClick={() => {
              setRejectModalOpen(false);
              setRejectReasonInput("");
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="ns_modal ns_reject_modal" onClick={(e) => e.stopPropagation()}>
              <div className="ns_modal_head">
                <h3>Reject – {headerName || "—"}</h3>
                <button
                  type="button"
                  className="ns_close"
                  onClick={() => {
                    setRejectModalOpen(false);
                    setRejectReasonInput("");
                  }}
                >
                  ×
                </button>
              </div>
              <div className="ns_modal_body">
                <p className="reject_desc">
                  Provide a reason for rejecting. This reason is required and can be shown to them when they try to
                  log in.
                </p>
                <div className="reject_reason">
                  <label>
                    <span>Rejection reason</span>
                    <textarea
                      value={rejectReasonInput}
                      onChange={(e) => setRejectReasonInput(e.target.value)}
                      placeholder="e.g. Incomplete documents, invalid registration"
                      className="reject_reason_input"
                      rows={3}
                    />
                  </label>
                </div>
                <div className="reject_actions">
                  <button
                    type="button"
                    className="btn_reject"
                    disabled={reviewActionBusy === "reject" || !String(rejectReasonInput || "").trim()}
                    onClick={submitRejectFromDetail}
                  >
                    {reviewActionBusy === "reject" ? "…" : "Reject"}
                  </button>
                  <button
                    type="button"
                    className="btn_cancel"
                    onClick={() => {
                      setRejectModalOpen(false);
                      setRejectReasonInput("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AdminLayout>
  );
}
